import React, { useState } from 'react';
import { Box, Link, Stack, Typography, useTheme } from '@mui/material';
import { Button, Skeleton } from '@repo/ui';
import { Download, Globe, Link2, Mail, Phone } from 'lucide-react';
import type { ProductContactCard } from '@repo/types';

import {
  FacebookIcon,
  InstagramIcon,
  LinkedinIcon,
  YoutubeIcon,
} from 'components/UI/BrandIcons';
import { EntityAvatar } from 'components/UI/EntityAvatar';
import { SectionLabel } from 'components/UI/SectionLabel';
import { useSnackbar } from 'components/Snackbar';
import { requestContactCardUrl, useContactCard } from 'hooks/api/useContactCard';
import { border, font, space } from 'settings/tokens';

/**
 * VRG-720 slice 3 -- the Contact section of the certificate page.
 *
 * Shown to a viewer who has access to the product's contact card (the owner after a claim, a
 * visitor who has passed the access gate, anyone when the product has no gate) and to nobody
 * else: the card never reaches the browser otherwise, so there is nothing here to hide. A
 * product without a card renders nothing at all.
 *
 * "Save contact" asks the API for a short-lived signed link and NAVIGATES to it. The link answers
 * a `text/vcard` attachment, which iOS and Android hand to their contacts app ("Add to
 * Contacts") with every field populated, photo and social links included. It is a plain
 * navigation, not a fetch-and-blob, because the OS only opens the contacts handler for a
 * top-level load of the file.
 */

const SOCIAL_LABEL: Record<string, string> = {
  linkedin: 'LinkedIn',
  instagram: 'Instagram',
  x: 'X',
  tiktok: 'TikTok',
  facebook: 'Facebook',
  youtube: 'YouTube',
  other: 'Link',
};

/** A brand mark where we own one (`BrandIcons`); the generic link otherwise. */
const socialIcon = (network: string) => {
  switch (network) {
    case 'linkedin':
      return <LinkedinIcon size={20} strokeWidth={1.5} />;
    case 'instagram':
      return <InstagramIcon size={20} strokeWidth={1.5} />;
    case 'facebook':
      return <FacebookIcon size={20} strokeWidth={1.5} />;
    case 'youtube':
      return <YoutubeIcon size={20} strokeWidth={1.5} />;
    default:
      return <Link2 size={20} strokeWidth={1.5} />;
  }
};

const prettyUrl = (url: string) => url.replace(/^https?:\/\/(www\.)?/i, '').replace(/\/$/, '');

type RowProps = {
  icon: React.ReactNode;
  label: string;
  value: string;
  href: string;
  external?: boolean;
};

/** One tappable detail: icon, a small caption, the value. The whole row is the link. */
const DetailRow = ({ icon, label, value, href, external }: RowProps) => {
  const theme = useTheme();
  return (
    <Link
      href={href}
      {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      underline='none'
      color='inherit'
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: `${space.md}px`,
        minHeight: 56,
        // `!important`: index.css's `#vera-shadow-root *` border rule beats an sx class.
        borderBottom: `1px solid ${border.subtle} !important`,
        '&:last-of-type': { borderBottom: '1px solid transparent !important' },
        '&:focus-visible': {
          outline: `2px solid ${theme.palette.text.primary}`,
          outlineOffset: '-2px',
        },
      }}
    >
      <Box sx={{ width: 28, display: 'flex', flexShrink: 0, color: theme.palette.text.primary }}>
        {icon}
      </Box>
      <Stack sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: font.label, color: 'text.secondary' }}>{label}</Typography>
        <Typography noWrap sx={{ fontSize: font.body }}>
          {value}
        </Typography>
      </Stack>
    </Link>
  );
};

const ContactCardSkeleton = () => (
  <Stack spacing={2} sx={{ pt: 3 }}>
    <Skeleton width={72} height={14} />
    <Stack direction='row' alignItems='center' spacing={2}>
      <Skeleton variant='circular' width={64} height={64} />
      <Stack spacing={1}>
        <Skeleton width={160} height={20} />
        <Skeleton width={120} height={14} />
      </Stack>
    </Stack>
    <Skeleton variant='rounded' height={40} />
  </Stack>
);

export const ContactCardSection: React.FC<{
  certificateId: string;
  /** Whether the server could release the card to this viewer (owner, or an ungated product). */
  canRequest: boolean;
}> = ({ certificateId, canRequest }) => {
  const { data: card, isLoading } = useContactCard(certificateId, canRequest);
  const { openSnackbar } = useSnackbar();
  const [saving, setSaving] = useState(false);

  if (isLoading) return <ContactCardSkeleton />;
  if (!card) return null;

  const c: ProductContactCard = card;
  const subtitle = [c.title, c.company].filter(Boolean).join(' at ');

  const handleSave = async () => {
    setSaving(true);
    try {
      const url = await requestContactCardUrl(certificateId);
      // A top-level navigation: the OS opens Add to Contacts for the vCard attachment.
      window.location.href = url;
    } catch {
      openSnackbar({
        severity: 'error',
        title: 'Could not save this contact',
        text: 'Please try again.',
      });
    } finally {
      // The page stays mounted behind the contacts sheet, so the button comes back.
      setSaving(false);
    }
  };

  return (
    <Stack id='contact-card-section' sx={{ pt: 3 }}>
      {/* No `(i)` here: `contact-card` is a brand-floor glossary term and this is the consumer
          surface, where TermInfo would render nothing (working-conventions section 59). */}
      <SectionLabel first>Contact</SectionLabel>

      <Stack direction='row' alignItems='center' spacing={2} sx={{ mb: `${space.md}px` }}>
        <EntityAvatar entity='person' src={c.photo_url} name={c.name || c.company} size={64} />
        <Stack sx={{ minWidth: 0 }}>
          {c.name && (
            <Typography sx={{ fontSize: font.heading, fontWeight: 500, lineHeight: 1.25 }}>
              {c.name}
            </Typography>
          )}
          {subtitle && (
            <Typography sx={{ fontSize: font.body, color: 'text.secondary', lineHeight: 1.4 }}>
              {subtitle}
            </Typography>
          )}
        </Stack>
      </Stack>

      <Stack>
        {c.phone && (
          <DetailRow
            icon={<Phone size={20} strokeWidth={1.5} />}
            label='Phone'
            value={c.phone}
            href={`tel:${c.phone.replace(/[^+\d]/g, '')}`}
          />
        )}
        {c.email && (
          <DetailRow
            icon={<Mail size={20} strokeWidth={1.5} />}
            label='Email'
            value={c.email}
            href={`mailto:${c.email}`}
          />
        )}
        {c.website && (
          <DetailRow
            icon={<Globe size={20} strokeWidth={1.5} />}
            label='Website'
            value={prettyUrl(c.website)}
            href={c.website}
            external
          />
        )}
        {(c.socials ?? []).map((s, i) => (
          // The network NAME is always the caption, never a bare URL as the only label.
          <DetailRow
            key={`${s.network}-${i}`}
            icon={socialIcon(s.network)}
            label={SOCIAL_LABEL[s.network] ?? 'Link'}
            value={prettyUrl(s.url)}
            href={s.url}
            external
          />
        ))}
      </Stack>

      <Button
        variant='secondary'
        fullWidth
        loading={saving}
        startIcon={<Download size={18} strokeWidth={1.5} />}
        onClick={handleSave}
        sx={{ mt: `${space.md}px` }}
      >
        Save contact
      </Button>
    </Stack>
  );
};
